import { ALMOST_SOLD_OUT_AT, FILLING_UP_AT } from "@tripcart/shared";

import type { PluralTranslator, Translator } from "@/lib/translations";

/** The style guide's scarcity bands (`.pl-crit` / `.pl-warn`). They live in
 *  `@tripcart/shared` (`departure-scarcity.ts`) so the Departure Dates block
 *  reads the same two numbers. */
export { ALMOST_SOLD_OUT_AT, FILLING_UP_AT };

/** The pill's colour band. The package card maps each to its module class. */
export type AvailabilityPillTone = "neutral" | "crit" | "warn" | "ok";

export interface AvailabilityPill {
  label: string;
  tone: AvailabilityPillTone;
}

export function resolveAvailabilityPill({
  isNotScheduled,
  isSoldOut,
  showSeatCount,
  spotsLeft,
  t,
  tn,
}: {
  isNotScheduled: boolean;
  isSoldOut: boolean;
  showSeatCount: boolean;
  spotsLeft: number | null;
  t: Translator;
  tn: PluralTranslator;
}): AvailabilityPill | null {
  // Neutral, and NOT the sold-out treatment. The package does not run today;
  // nothing is full.
  if (isNotScheduled) {
    return {
      label: t("Not available on this date"),
      tone: "neutral",
    };
  }

  // NO PILL when sold out — the guide's sold-out card carries none. It says so
  // three louder ways instead: a danger edge on the card, a dashed "Sold out"
  // block where the Book button would be, and a note across the bottom naming
  // the date. A fourth label beside the title was ours, and it competed with
  // all three.
  if (isSoldOut) return null;

  // Gates the WHOLE pill, not just a number — the name predates the healthy
  // arm below dropping its count. False here means the package is uncapped
  // (sold-out and not-scheduled are already answered above), so there is no
  // bounded figure to band against.
  //
  // Worth knowing: an uncapped DEPARTURE does say "Available"
  // (`status-pill.tsx`'s `uncapped` arm) while an uncapped package says
  // nothing. That predates this change and is left alone deliberately rather
  // than widened during a review pass.
  if (!showSeatCount || spotsLeft === null) return null;

  // Guide `.pl-crit` / `.pl-warn`, and the guide's own pairs:
  //   `.pl-crit` → `--danger-bg` + `--danger-fg`     (#fef2f2 / #b91c1c)
  //   `.pl-warn` → `--warn-bg-mid` + `--warn-fg-dark` (#fdebc8 / #92400e)
  //
  // Both tiers briefly sat in the warning family here, on the argument that a
  // scarcity notice is an invitation to hurry rather than a failure and this
  // card already spends red on blocking states. The guide disagrees: it
  // escalates the ≤2 band into danger, which is what makes the two tiers read
  // as two different things rather than as one tint at two weights.
  //
  // Identical to `status-pill.tsx`'s TONES — the departures list and these
  // cards sit on one screen, so the same seat count must not wear two colours
  // any more than two wordings.
  if (spotsLeft <= ALMOST_SOLD_OUT_AT) {
    return {
      label: tn(
        "Almost sold out · 1 spot left",
        "Almost sold out · {n} spots left",
        spotsLeft,
        { n: spotsLeft },
      ),
      tone: "crit",
    };
  }

  if (spotsLeft <= FILLING_UP_AT) {
    return {
      label: tn(
        "Filling up fast · 1 spot left",
        "Filling up fast · {n} spots left",
        spotsLeft,
        { n: spotsLeft },
      ),
      tone: "warn",
    };
  }

  // Guide `.pl-ok`: "Available", with NO count. The guide has ONE pill
  // vocabulary for every surface — `depBits` yields exactly `Sold out` /
  // `Almost sold out · N` / `Filling up fast · N` / `Available` — and says why
  // beside the branch: "A spot count only earns its space once the date is
  // running out; on an open date the number is noise."
  //
  // This printed the raw count until 2026-09-21, when the departure row's
  // identical arm was corrected and this one was missed. That left "Available"
  // on a departure row and "20 seats available" on a package card ON THE SAME
  // SCREEN — precisely the drift this module and `status-pill.tsx` both carry
  // docstrings promising to prevent. The two bands below five keep their
  // counts, here as there, because there the number is what makes the pill
  // actionable.
  return {
    label: t("Available"),
    tone: "ok",
  };
}
